/** The two factors every conversion here is built from; their inverses are derived, not rounded. */
const KG_PER_LB = 0.453592;
const METERS_PER_INCH = 0.0254;
export const INCHES_PER_FOOT = 12;
export const LBS_PER_KG = 1 / KG_PER_LB;
export const INCHES_PER_METER = 1 / METERS_PER_INCH;

export const toHeightInches = (
  heightRaw: string | undefined,
  heightInchesRaw: string | undefined,
  heightUnit: string
): number | null => {
  const raw = parseFloat(heightRaw ?? '');
  if (!raw || raw <= 0) return null;

  if (heightUnit === 'ft') {
    const extra = parseInt(heightInchesRaw ?? '0', 10);
    return raw * INCHES_PER_FOOT + extra;
  }

  return raw * INCHES_PER_METER;
};

export const toWeightLbs = (weightRaw: string | undefined, weightUnit: string): number | null => {
  const raw = parseFloat(weightRaw ?? '');
  if (!raw || raw <= 0) return null;
  return weightUnit === 'lb' ? raw : raw * LBS_PER_KG;
};

interface IBmiInput {
  height?: string;
  heightInches?: string;
  weight?: string;
  heightUnit: string;
  weightUnit: string;
}

/**
 * Height in metres for the BMI. Unlike `toHeightInches`, empty inches count as 0 and feet may be 0
 * when inches are given, as the onboarding BMI always did.
 */
const toHeightMeters = (
  heightRaw: string | undefined,
  heightInchesRaw: string | undefined,
  heightUnit: string
): number | null => {
  const raw = parseFloat(heightRaw ?? '');
  if (heightUnit !== 'ft') return !raw || raw <= 0 ? null : raw;
  const inches = parseInt(heightInchesRaw || '0', 10);
  if (!raw && !inches) return null;
  return (raw * INCHES_PER_FOOT + inches) * METERS_PER_INCH;
};

/** Weight in kilograms, from the same inputs as `toWeightLbs`. */
const toWeightKg = (weightRaw: string | undefined, weightUnit: string): number | null => {
  const raw = parseFloat(weightRaw ?? '');
  if (!raw || raw <= 0) return null;
  return weightUnit === 'lb' ? raw * KG_PER_LB : raw;
};

export const calcBmi = ({ height, heightInches, weight, heightUnit, weightUnit }: IBmiInput) => {
  const meters = toHeightMeters(height, heightInches, heightUnit);
  const kg = toWeightKg(weight, weightUnit);
  if (!meters || !kg) return '';
  return (kg / (meters * meters)).toFixed(1);
};

/** A stored height record (m, ft or in) in inches. */
export const heightRecordToInches = (value: number, unit: string): number | null => {
  if (unit === 'in') return value;
  if (unit === 'ft' || unit === 'm') return toHeightInches(String(value), '0', unit);
  return null;
};

/** A stored weight record (kg or lb) in pounds. */
export const weightRecordToLbs = (value: number, unit: string): number | null => {
  if (unit !== 'lb' && unit !== 'kg') return null;
  return toWeightLbs(String(value), unit);
};

/** Inches rendered as the form fields of the given height unit (ft as feet and inches, else m). */
export const formatHeightForUnit = (inches: number, unit: string) => {
  if (unit === 'ft') {
    const totalInches = Math.round(inches);
    return {
      height: String(Math.floor(totalInches / INCHES_PER_FOOT)),
      heightInches: String(totalInches % INCHES_PER_FOOT)
    };
  }
  return { height: (inches / INCHES_PER_METER).toFixed(2), heightInches: '' };
};

export const formatWeightForUnit = (lbs: number, unit: string): string => {
  const value = unit === 'lb' ? lbs : lbs / LBS_PER_KG;
  return value.toFixed(1);
};
